import React, { useState } from 'react'
import { Button } from '../shared/Button'
import { crmToast } from './crmUi'
import { obSetRisk, type ObConfig, type Onboarding } from './crmApi'

/**
 * AT RISK panel (Onboardings Phase 5, from the sandbox). Staff-only. Status,
 * multi-select trigger reasons (+ Other), REQUIRED owner, auto-filled last
 * login/quote/accepted, notes, and the primary "Book account review call".
 */

export const RISK_COLOR: Record<string, string> = { Watch: '#b45309', 'At Risk': '#c2410c', Urgent: '#b91c1c', Reviewed: '#047857' }

const fmtD = (s?: string | null) => (s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown')

export const ObRiskPanel: React.FC<{
  ob: Onboarding
  cfg: ObConfig
  users: any[]
  onClose: () => void
  onSaved: () => void
  onBookReview: () => void
}> = ({ ob, cfg, users, onClose, onSaved, onBookReview }) => {
  const [status, setStatus] = useState<string>(ob.risk?.status || (ob.suggestions.length ? 'Watch' : 'Watch'))
  const [reasons, setReasons] = useState<string[]>(ob.risk?.reasons || ob.suggestions)
  const [other, setOther] = useState(ob.risk?.other || '')
  const [ownerId, setOwnerId] = useState(ob.risk?.ownerId || ob.ownerId || '')
  const [notes, setNotes] = useState(ob.risk?.notes || '')
  const [saving, setSaving] = useState(false)
  const inputCls = 'rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400'

  const save = (clear = false) => {
    if (!clear && !ownerId) { crmToast('An owner is required', 'error'); return }
    setSaving(true)
    obSetRisk(ob.id, clear ? { status: null } : { status, reasons, other: other.trim() || null, ownerId, notes: notes.trim() || null })
      .then(() => { crmToast(clear ? 'Risk cleared' : 'Risk saved', 'success'); onSaved(); onClose() })
      .catch((e: any) => crmToast(e?.message || 'Could not save', 'error'))
      .finally(() => setSaving(false))
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,.5)' }} onClick={onClose}>
      <div className="w-[480px] max-h-[85vh] overflow-y-auto rounded-xl bg-surface border border-border shadow-xl p-5 space-y-3.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="text-[15px] font-bold text-txt-primary">At Risk — {ob.companyName}</p>
          <button onClick={onClose} className="text-txt-tertiary hover:text-txt-primary">✕</button>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-txt-tertiary mb-1.5">Status</p>
          <div className="flex gap-1.5 flex-wrap">
            {cfg.riskStatuses.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-2.5 py-1.5 rounded-lg text-[12px] font-bold border transition-colors ${status === s ? 'text-white' : 'bg-surface text-txt-secondary border-border hover:bg-surface-hover'}`}
                style={status === s ? { background: RISK_COLOR[s], borderColor: RISK_COLOR[s] } : undefined}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-txt-tertiary mb-1.5">Reasons</p>
          <div className="space-y-1">
            {cfg.riskTriggers.map((t) => (
              <label key={t.key} className="flex items-center gap-2 text-[12px] text-txt-primary cursor-pointer">
                <input
                  type="checkbox"
                  checked={reasons.includes(t.key)}
                  onChange={(e) => setReasons((r) => (e.target.checked ? [...r, t.key] : r.filter((x) => x !== t.key)))}
                  className="w-4 h-4 accent-amber-600"
                />
                {t.key} <span className="text-txt-tertiary">· {t.hint}</span>
              </label>
            ))}
            <input value={other} onChange={(e) => setOther(e.target.value)} placeholder="Other reason…" className={`${inputCls} w-full mt-1`} />
          </div>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-txt-tertiary mb-1">Owner <span className="text-red-600">*</span></p>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className={`${inputCls} w-full`}>
            <option value="">Choose an owner…</option>
            {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </select>
        </div>

        <div className="rounded-lg border border-border bg-surface-secondary/60 p-2.5 grid grid-cols-3 gap-2 text-center">
          {[['Last login', ob.lastLoginAt], ['Last quote', ob.lastQuoteAt], ['Last accepted', ob.lastQuoteAcceptedAt]].map(([label, v]) => (
            <div key={label as string}>
              <p className="text-[9px] font-bold uppercase tracking-wider text-txt-tertiary">{label}</p>
              <p className="text-[11px] font-semibold text-txt-primary mt-0.5">{fmtD(v as string | null)}</p>
            </div>
          ))}
        </div>

        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes…" rows={3} className={`${inputCls} w-full resize-none`} />

        <Button className="w-full" onClick={onBookReview}>Book account review call</Button>

        <div className="flex items-center justify-between gap-2 pt-1">
          {ob.risk ? (
            <button onClick={() => save(true)} className="text-[12px] font-semibold text-red-600 hover:underline">Clear risk</button>
          ) : <span />}
          <span className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={() => save(false)} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </span>
        </div>
      </div>
    </div>
  )
}
